/**
 * PersonaConversationPanel — Left panel of the persona discovery wizard.
 * Editorial input style: borderless fields, warm tint on hover, market ruler
 * on focus. AI helpers sit as quiet sparkle icons that expand on hover.
 */

import { useState } from 'react'
import { Check, Plus, Sparkles, X } from 'lucide-react'
import type { PersonaData, PersonaType, PersonaWorkshopPhaseId, PersonaSuggestion, JtbdData, JtbdPainData, JtbdGainData } from '@ispot/shared'

interface Props {
  phaseId: PersonaWorkshopPhaseId
  phaseIndex: number
  phaseLabel: string
  personaType: PersonaType
  personaData: PersonaData
  suggestions: PersonaSuggestion[]
  archetypes: string[]
  demographics: Record<string, string[]>
  quotes: string[]
  isSuggestionsLoading: boolean
  /** When set, all JTBD phases operate on jtbds[targetJtbdIndex] instead of jtbds[0]. */
  targetJtbdIndex?: number
  onUpdatePersonaData: (updates: Partial<PersonaData>) => void
  /** Update the session's persona type (User vs Buyer). Surfaced in the Identity phase. */
  onUpdatePersonaType?: (type: PersonaType) => void
}

const PHASE_QUESTIONS: Record<PersonaType, Record<PersonaWorkshopPhaseId, { question: string; hint: string }>> = {
  user: {
    identity: { question: "Let's bring your persona to life. Who are they?", hint: 'Start with a name and role. The AI will suggest an archetype.' },
    goals: { question: "What's the primary job {name} is trying to get done?", hint: 'Focus on ONE main job. What is the most important thing they need to accomplish?' },
    pains: { question: "What prevents {name} from getting this job done?", hint: 'What frustrates them? What slows them down? Think about friction blocking the job.' },
    behaviors: { question: "What does success look like for {name}?", hint: 'What outcomes would make them feel the job is done well?' },
    tools: { question: "What tools does {name} rely on?", hint: 'List the apps and platforms they use daily.' },
    voice: { question: "How does {name} talk about their challenges?", hint: 'Pick or write a characteristic quote.' },
    segment: { question: "What customer segment does {name} belong to?", hint: 'Define the segment and key attributes.' },
    relationships: { question: "Who does {name} interact with?", hint: 'Map relationships to other personas in the organization.' },
  },
  buyer: {
    identity: { question: "Who is the decision-maker? Let's define them.", hint: 'Start with a name and title. The AI will suggest a buyer archetype.' },
    goals: { question: "What's the primary job {name} is hiring a solution for?", hint: 'Focus on ONE main outcome they want. What business result are they after?' },
    pains: { question: "What makes achieving this outcome hard for {name}?", hint: 'What blocks or delays them? Think about internal politics, risk, and complexity.' },
    behaviors: { question: "What does a successful outcome look like for {name}?", hint: 'What results would justify the investment? Think ROI, team impact, strategic wins.' },
    tools: { question: "What tools and platforms does {name} evaluate with?", hint: 'Analyst reports, peer networks, review sites they trust.' },
    voice: { question: "How does {name} describe the buying challenge?", hint: 'Pick or write a characteristic quote.' },
    segment: { question: "What market segment does {name} represent?", hint: 'Define the segment and buying characteristics.' },
    relationships: { question: "Who else is involved in the buying decision?", hint: 'Map the buying committee and influence dynamics.' },
  },
}

const DEFAULT_JTBD_SUGGESTIONS: Record<PersonaType, PersonaSuggestion[]> = {
  user: [
    { text: 'Ship quality features faster with fewer blockers', fromProject: false },
    { text: 'Reduce repetitive manual work to focus on what matters', fromProject: false },
    { text: 'Make confident decisions based on reliable data', fromProject: false },
    { text: 'Collaborate effectively across distributed teams', fromProject: false },
  ],
  buyer: [
    { text: 'Consolidate fragmented tooling to reduce operational costs', fromProject: false },
    { text: 'Improve team productivity and measure the impact', fromProject: false },
    { text: 'Meet compliance requirements without slowing delivery', fromProject: false },
    { text: 'Accelerate time-to-market for new products', fromProject: false },
  ],
}

const DEFAULT_PAIN_SUGGESTIONS: Record<PersonaType, PersonaSuggestion[]> = {
  user: [
    { text: 'Legacy systems slow everything down', fromProject: false },
    { text: 'Too many context switches between tools', fromProject: false },
    { text: 'Unclear requirements from stakeholders', fromProject: false },
    { text: 'Manual processes eat into productive time', fromProject: false },
    { text: 'Hard to stay on top of changing priorities', fromProject: false },
  ],
  buyer: [
    { text: 'Hard to get budget approval across departments', fromProject: false },
    { text: 'Too many stakeholders with conflicting priorities', fromProject: false },
    { text: 'Unclear ROI compared to current solution', fromProject: false },
    { text: 'Security and compliance review takes months', fromProject: false },
    { text: 'Fear of disrupting existing workflows', fromProject: false },
  ],
}

const DEFAULT_OUTCOME_SUGGESTIONS: Record<PersonaType, PersonaSuggestion[]> = {
  user: [
    { text: 'Spend more time building, less time on process', fromProject: false },
    { text: 'Ship with confidence — fewer rollbacks and hotfixes', fromProject: false },
    { text: 'Clear visibility into what matters most right now', fromProject: false },
    { text: 'One source of truth everyone trusts', fromProject: false },
    { text: 'Onboard new team members quickly', fromProject: false },
  ],
  buyer: [
    { text: 'Measurable productivity gains within 90 days', fromProject: false },
    { text: 'Lower total cost of ownership vs current stack', fromProject: false },
    { text: 'Team adoption without heavy change management', fromProject: false },
    { text: 'Clear audit trail for compliance reporting', fromProject: false },
    { text: 'Vendor that grows with our needs', fromProject: false },
  ],
}

const DEFAULT_ARCHETYPES: Record<PersonaType, string[]> = {
  user: ['"The Pragmatic Builder"', '"The Power User"', '"The Reluctant Adopter"'],
  buyer: ['"The ROI-Driven Executive"', '"The Consensus Builder"', '"The Risk-Averse Evaluator"'],
}

// Editorial input style: borderless, warm tint on hover, market ruler on focus.
const inputCls = 'w-full rounded bg-transparent px-2 py-1.5 text-sm text-text transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] focus:outline-none placeholder:text-text-muted'
const textareaCls = `${inputCls} resize-y leading-relaxed`

const fieldLabelCls = 'mb-1.5 block text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted'

// ============================================================================
// HELPERS
// ============================================================================

function getTargetJtbd(personaData: PersonaData, index: number): JtbdData {
  return personaData.jtbds?.[index] || {
    id: `jtbd-${index}`,
    title: '',
    functional: '',
    pains: [],
    gains: [],
  }
}

function updateTargetJtbd(
  personaData: PersonaData,
  index: number,
  updates: Partial<JtbdData>,
): JtbdData[] {
  const existing = personaData.jtbds || []
  if (index < 0 || index > existing.length) return existing
  const next = existing.slice()
  const base = next[index] || {
    id: `jtbd-${Date.now()}`,
    title: '',
    functional: '',
    pains: [],
    gains: [],
  }
  next[index] = { ...base, ...updates }
  return next
}

// ============================================================================
// COMPONENT
// ============================================================================

export function PersonaConversationPanel({
  phaseId, phaseIndex, phaseLabel, personaType, personaData,
  suggestions, archetypes, demographics: _demographics, quotes,
  isSuggestionsLoading, targetJtbdIndex, onUpdatePersonaData, onUpdatePersonaType,
}: Props) {
  const [customInput, setCustomInput] = useState('')
  const config = PHASE_QUESTIONS[personaType][phaseId]
  const name = personaData.name || 'this persona'
  const jtbdIndex = targetJtbdIndex ?? 0
  const isSecondary = jtbdIndex > 0
  const jobLabel = isSecondary ? `secondary job #${jtbdIndex + 1}` : 'primary job'
  const jobLabelTitle = isSecondary ? `Secondary Job #${jtbdIndex + 1}` : 'Primary Job'
  const rawQuestion = config.question.replace('{name}', name)
  const question = isSecondary
    ? rawQuestion.replace(/primary job/gi, jobLabel)
    : rawQuestion

  const effectiveArchetypes = archetypes.length > 0
    ? archetypes
    : DEFAULT_ARCHETYPES[personaType]

  // ── Identity phase ──────────────────────────────────────────────────
  if (phaseId === 'identity') {
    return (
      <div>
        <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />
        <div className="flex flex-col gap-4">
          {onUpdatePersonaType && (
            <div>
              <label className={fieldLabelCls}>Persona type</label>
              <div className="inline-flex items-center gap-0.5 rounded-md bg-warm-100 p-0.5">
                {(['user', 'buyer'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => onUpdatePersonaType(t)}
                    className={`rounded px-3 py-1 text-[12.5px] font-medium transition-colors ${
                      personaType === t
                        ? 'bg-bg-card text-text shadow-[0_1px_0_rgba(29,26,21,0.04)]'
                        : 'text-text-muted hover:text-text-secondary'
                    }`}
                    title={t === 'user' ? 'Someone who uses the product' : 'Someone who buys or approves the product'}
                  >
                    {t === 'user' ? 'User' : 'Buyer'}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[12px] text-text-muted">
                {personaType === 'buyer'
                  ? 'A buyer evaluates and approves the purchase. Drives ROI and risk decisions.'
                  : 'A user works with the product day to day. Drives adoption and feature feedback.'}
              </p>
            </div>
          )}
          <div>
            <label className={fieldLabelCls}>Full name</label>
            <input
              className={inputCls}
              placeholder="e.g. Sarah Chen"
              value={personaData.name}
              onChange={e => onUpdatePersonaData({ name: e.target.value })}
            />
          </div>
          <div>
            <label className={fieldLabelCls}>Role / Title</label>
            <input
              className={inputCls}
              placeholder={personaType === 'buyer' ? 'e.g. VP of Procurement' : 'e.g. VP of Engineering'}
              value={personaData.role}
              onChange={e => onUpdatePersonaData({ role: e.target.value })}
            />
          </div>
          {effectiveArchetypes.length > 0 && (
            <div>
              <SuggestEyebrow label="AI-suggested archetype" />
              <div className="flex flex-wrap gap-1.5">
                {effectiveArchetypes.map(a => {
                  const isSelected = personaData.archetype === a
                  return (
                    <button
                      key={a}
                      onClick={() => onUpdatePersonaData({ archetype: a })}
                      className={`rounded-full px-3.5 py-1.5 text-[12.5px] transition-colors ${
                        isSelected
                          ? 'bg-market text-white'
                          : 'bg-warm-100 text-text-secondary hover:bg-warm-200 hover:text-text'
                      }`}
                    >
                      {a}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Job to Be Done phase (was "goals") ──────────────────────────────
  if (phaseId === 'goals') {
    const primaryJtbd = getTargetJtbd(personaData, jtbdIndex)
    const effectiveSuggestions = suggestions.length > 0
      ? suggestions
      : DEFAULT_JTBD_SUGGESTIONS[personaType]

    const handleSelectJtbd = (text: string) => {
      onUpdatePersonaData({
        jtbds: updateTargetJtbd(personaData, jtbdIndex, {
          id: primaryJtbd.id || 'primary',
          title: text,
          functional: primaryJtbd.functional || text,
          pains: primaryJtbd.pains || [],
          gains: primaryJtbd.gains || [],
        }),
      })
    }

    return (
      <div>
        <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />

        <div className="mb-5">
          <label className={fieldLabelCls}>{jobLabelTitle}</label>
          <input
            className={inputCls}
            placeholder={personaType === 'buyer' ? 'e.g. Consolidate tools to reduce costs' : 'e.g. Ship features faster without breaking things'}
            value={primaryJtbd.title}
            onChange={e => onUpdatePersonaData({
              jtbds: updateTargetJtbd(personaData, jtbdIndex, { title: e.target.value }),
            })}
          />
        </div>

        <div className="mb-5">
          <label className={fieldLabelCls}>
            What does getting this job done look like? <span className="font-normal normal-case tracking-normal text-text-muted">(optional)</span>
          </label>
          <textarea
            className={textareaCls}
            placeholder="Describe the functional outcome — what specifically needs to happen?"
            value={primaryJtbd.functional}
            rows={3}
            onChange={e => onUpdatePersonaData({
              jtbds: updateTargetJtbd(personaData, jtbdIndex, { functional: e.target.value }),
            })}
          />
        </div>

        <SuggestionChips
          eyebrow="Job suggestions"
          suggestions={effectiveSuggestions}
          selectedTexts={primaryJtbd.title ? [primaryJtbd.title] : []}
          isLoading={isSuggestionsLoading}
          onSelect={handleSelectJtbd}
        />
      </div>
    )
  }

  // ── Key Pains phase ─────────────────────────────────────────────────
  if (phaseId === 'pains') {
    const primaryJtbd = getTargetJtbd(personaData, jtbdIndex)
    const painItems = primaryJtbd.pains || []
    const effectiveSuggestions = suggestions.length > 0
      ? suggestions
      : DEFAULT_PAIN_SUGGESTIONS[personaType]

    const handleAddPain = (text: string) => {
      if (painItems.some(p => p.description === text)) return
      const severity: JtbdPainData['severity'] = painItems.length === 0 ? 'high' : painItems.length === 1 ? 'medium' : 'low'
      const newPain: JtbdPainData = { id: `pain-${Date.now()}`, description: text, severity, evidenceCount: 0 }
      onUpdatePersonaData({
        jtbds: updateTargetJtbd(personaData, jtbdIndex, { pains: [...painItems, newPain] }),
      })
    }

    const handleRemovePain = (painId: string) => {
      onUpdatePersonaData({
        jtbds: updateTargetJtbd(personaData, jtbdIndex, { pains: painItems.filter(p => p.id !== painId) }),
      })
    }

    return (
      <div>
        <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />

        {primaryJtbd.title && <JobContextStrip label={jobLabelTitle} title={primaryJtbd.title} />}

        <SuggestionChips
          eyebrow="Suggestions"
          suggestions={effectiveSuggestions}
          selectedTexts={painItems.map(p => p.description)}
          isLoading={isSuggestionsLoading}
          onSelect={handleAddPain}
        />

        <CustomInput
          placeholder="Add a pain point…"
          value={customInput}
          onChange={setCustomInput}
          onAdd={() => { handleAddPain(customInput.trim()); setCustomInput('') }}
        />

        {painItems.length > 0 && (
          <ul className="m-0 list-none space-y-0.5 p-0">
            {painItems.map(p => (
              <SelectedItem key={p.id} text={p.description} onRemove={() => handleRemovePain(p.id)} />
            ))}
          </ul>
        )}
      </div>
    )
  }

  // ── Desired Outcomes phase (was "behaviors") ────────────────────────
  if (phaseId === 'behaviors') {
    const primaryJtbd = getTargetJtbd(personaData, jtbdIndex)
    const gainItems = primaryJtbd.gains || []
    const effectiveSuggestions = suggestions.length > 0
      ? suggestions
      : DEFAULT_OUTCOME_SUGGESTIONS[personaType]

    const handleAddGain = (text: string) => {
      if (gainItems.some(g => g.description === text)) return
      const importance: JtbdGainData['importance'] = gainItems.length === 0 ? 'must_have' : gainItems.length === 1 ? 'expected' : 'nice_to_have'
      const newGain: JtbdGainData = { id: `gain-${Date.now()}`, description: text, importance, evidenceCount: 0 }
      onUpdatePersonaData({
        jtbds: updateTargetJtbd(personaData, jtbdIndex, { gains: [...gainItems, newGain] }),
      })
    }

    const handleRemoveGain = (gainId: string) => {
      onUpdatePersonaData({
        jtbds: updateTargetJtbd(personaData, jtbdIndex, { gains: gainItems.filter(g => g.id !== gainId) }),
      })
    }

    return (
      <div>
        <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />

        {primaryJtbd.title && <JobContextStrip label={jobLabelTitle} title={primaryJtbd.title} />}

        <SuggestionChips
          eyebrow="Suggestions"
          suggestions={effectiveSuggestions}
          selectedTexts={gainItems.map(g => g.description)}
          isLoading={isSuggestionsLoading}
          onSelect={handleAddGain}
        />

        <CustomInput
          placeholder="Add a desired outcome…"
          value={customInput}
          onChange={setCustomInput}
          onAdd={() => { handleAddGain(customInput.trim()); setCustomInput('') }}
        />

        {gainItems.length > 0 && (
          <ul className="m-0 list-none space-y-0.5 p-0">
            {gainItems.map(g => (
              <SelectedItem key={g.id} text={g.description} onRemove={() => handleRemoveGain(g.id)} />
            ))}
          </ul>
        )}
      </div>
    )
  }

  // ── Voice phase (deep mode) ─────────────────────────────────────────
  if (phaseId === 'voice') {
    return (
      <div>
        <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />
        {quotes.length > 0 && (
          <div className="mb-4 flex flex-col gap-1">
            {quotes.map((q, i) => {
              const isSelected = personaData.quote === q
              return (
                <button
                  key={i}
                  onClick={() => onUpdatePersonaData({ quote: q })}
                  className={`rounded p-3 text-left text-[13px] italic leading-relaxed transition-colors ${
                    isSelected
                      ? 'border-l-2 border-market bg-warm-100 text-text'
                      : 'border-l-2 border-transparent bg-transparent text-text-secondary hover:bg-warm-100 hover:text-text'
                  }`}
                >
                  &ldquo;{q}&rdquo;
                </button>
              )
            })}
          </div>
        )}
        <textarea
          className={textareaCls}
          placeholder="Write a custom quote…"
          rows={3}
          value={personaData.quote || ''}
          onChange={e => onUpdatePersonaData({ quote: e.target.value })}
        />
      </div>
    )
  }

  // ── Fallback for segment/relationships (deep mode) ──────────────────
  return (
    <div>
      <PhaseHeader index={phaseIndex} label={phaseLabel} question={question} hint={config.hint} />
      <p className="text-sm italic text-text-muted">This phase is available in the detailed persona editor.</p>
    </div>
  )
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function PhaseHeader({ index, label, question, hint }: {
  index: number
  label: string
  question: string
  hint: string
}) {
  return (
    <div className="mb-7">
      <div className="mb-2 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-market">
        Step {index + 1} · {label}
      </div>
      <h2 className="font-display text-[22px] font-semibold leading-tight tracking-tight text-text">{question}</h2>
      <p className="mt-2 max-w-[60ch] text-[13px] leading-relaxed text-text-secondary">{hint}</p>
    </div>
  )
}

function SuggestEyebrow({ label }: { label: string }) {
  return (
    <div className="mb-2 inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-text-muted">
      <Sparkles className="h-3 w-3" />
      <span>{label}</span>
    </div>
  )
}

function JobContextStrip({ label, title }: { label: string; title: string }) {
  return (
    <div className="mb-5 border-l-2 border-market pl-3">
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-market">{label}</div>
      <div className="mt-0.5 text-[13.5px] font-medium text-text">{title}</div>
    </div>
  )
}

function SuggestionChips({ eyebrow, suggestions, selectedTexts, isLoading, onSelect }: {
  eyebrow: string
  suggestions: PersonaSuggestion[]
  selectedTexts: string[]
  isLoading: boolean
  onSelect: (text: string) => void
}) {
  return (
    <div className="mb-5">
      <SuggestEyebrow label={eyebrow} />
      {isLoading ? (
        <div className="flex flex-wrap gap-1.5">
          {[180, 140, 200].map((w, i) => (
            <div key={i} className="h-7 animate-pulse rounded-full bg-warm-100" style={{ width: w }} />
          ))}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map(s => {
            const isSelected = selectedTexts.includes(s.text)
            return (
              <button
                key={s.text}
                onClick={() => onSelect(s.text)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[12.5px] transition-colors ${
                  isSelected
                    ? 'bg-market text-white'
                    : 'bg-warm-100 text-text-secondary hover:bg-warm-200 hover:text-text'
                }`}
              >
                {s.fromProject && (
                  <span className={`h-1.5 w-1.5 rounded-full ${isSelected ? 'bg-white/70' : 'bg-market'}`} title="From project documents" />
                )}
                <span>{s.text}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function CustomInput({ placeholder, value, onChange, onAdd }: {
  placeholder: string
  value: string
  onChange: (v: string) => void
  onAdd: () => void
}) {
  return (
    <div className="mb-5 flex items-center gap-2">
      <Plus className="h-3.5 w-3.5 shrink-0 text-text-muted" />
      <input
        className="flex-1 rounded bg-transparent px-2 py-1.5 text-sm text-text outline-none transition-colors hover:bg-warm-100 focus:bg-bg-card focus:shadow-[inset_2px_0_0_var(--color-market),0_0_0_1px_var(--color-warm-300)] placeholder:text-text-muted"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && value.trim() && onAdd()}
      />
      <button
        onClick={onAdd}
        disabled={!value.trim()}
        className="rounded-md bg-market px-3 py-1.5 text-[12.5px] font-medium text-white transition-colors hover:bg-market-dark disabled:bg-warm-200 disabled:text-text-muted disabled:cursor-not-allowed"
      >
        Add
      </button>
    </div>
  )
}

function SelectedItem({ text, onRemove }: {
  text: string
  onRemove: () => void
}) {
  return (
    <li className="group flex items-center gap-2.5 rounded px-2 py-1.5 transition-colors hover:bg-warm-100">
      <Check className="h-3.5 w-3.5 shrink-0 text-market" />
      <span className="flex-1 text-[13px] text-text">{text}</span>
      <button
        onClick={onRemove}
        className="rounded p-1 text-text-muted opacity-0 transition-all hover:bg-error/10 hover:text-error group-hover:opacity-100"
        aria-label={`Remove ${text}`}
      >
        <X className="h-3 w-3" />
      </button>
    </li>
  )
}
